import { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ChevronLeft, ChevronRight, X } from 'lucide-react';
import Seo from '@/components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GalleryAlbumPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [album, setAlbum] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/gallery/albums/${slug}`)
      .then(({ data }) => {
        if (cancelled) return;
        setAlbum(data.album);
        setPhotos(data.photos || []);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 404) navigate('/gallery', { replace: true });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug, navigate]);

  const closeLightbox = useCallback(() => setLightboxIdx(null), []);
  const nextPhoto = useCallback(() => {
    setLightboxIdx((idx) => (idx === null ? null : (idx + 1) % photos.length));
  }, [photos.length]);
  const prevPhoto = useCallback(() => {
    setLightboxIdx((idx) =>
      idx === null ? null : (idx - 1 + photos.length) % photos.length,
    );
  }, [photos.length]);

  useEffect(() => {
    if (lightboxIdx === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowRight') nextPhoto();
      else if (e.key === 'ArrowLeft') prevPhoto();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightboxIdx, closeLightbox, nextPhoto, prevPhoto]);

  if (loading) {
    return (
      <div className="pt-28 pb-20 min-h-screen bg-[#FAF9F6]">
        <div className="container-custom px-6 md:px-12 lg:px-24 max-w-6xl mx-auto">
          <div className="h-8 w-48 bg-[#F5F5F0] rounded animate-pulse mb-6" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div key={i} className="aspect-square bg-[#F5F5F0] rounded-sm animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!album) return null;

  const active = lightboxIdx !== null ? photos[lightboxIdx] : null;

  return (
    <div className="pt-28 pb-20 min-h-screen bg-[#FAF9F6]" data-testid="gallery-album-page">
      <Seo
        title={album.title + ' — Gallery'}
        description={album.description || `${album.title} — photographs from the Whispering Wristwear studio in Paphos.`}
        path={`/gallery/${album.slug}`}
        image={album.cover_image || undefined}
      />

      <div className="container-custom px-6 md:px-12 lg:px-24 max-w-6xl mx-auto">
        <Link
          to="/gallery"
          className="inline-flex items-center gap-2 text-[#84A98C] hover:text-[#354F52] transition-colors mb-8"
          data-testid="back-to-gallery"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm uppercase tracking-widest">All albums</span>
        </Link>

        <header className="max-w-3xl mb-12">
          <p className="text-caption text-[#84A98C] mb-2">Album</p>
          <h1 className="heading-display text-3xl md:text-4xl text-[#354F52] mb-4 leading-tight">
            {album.title}
          </h1>
          {album.description && (
            <p className="text-[#52796F] leading-relaxed font-serif italic">
              {album.description}
            </p>
          )}
          <p className="text-xs text-[#84A98C] mt-3">
            {photos.length} photo{photos.length === 1 ? '' : 's'}
          </p>
        </header>

        {photos.length === 0 ? (
          <div className="text-center py-12 text-[#52796F]">
            This album is still being arranged. Come back soon.
          </div>
        ) : (
          <div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
            data-testid="gallery-photo-grid"
          >
            {photos.map((photo, idx) => (
              <button
                key={photo.id}
                type="button"
                onClick={() => setLightboxIdx(idx)}
                className="group relative aspect-square bg-[#F5F5F0] rounded-sm overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                data-testid={`gallery-photo-${idx}`}
              >
                <img
                  src={photo.image_url}
                  alt={photo.alt_text || photo.caption || album.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {photo.caption && (
                  <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-[#354F52]/85 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-xs text-[#FAF9F6] font-serif italic line-clamp-2">
                      {photo.caption}
                    </p>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {active && (
        <div
          className="fixed inset-0 z-50 bg-[#354F52]/95 flex items-center justify-center p-4"
          onClick={closeLightbox}
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-6 right-6 p-2 text-[#FAF9F6] hover:text-[#D4AF37] transition-colors"
            data-testid="lightbox-close"
            aria-label="Close"
          >
            <X className="w-6 h-6" strokeWidth={1.5} />
          </button>
          {photos.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  prevPhoto();
                }}
                className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 p-3 text-[#FAF9F6] hover:text-[#D4AF37] transition-colors"
                data-testid="lightbox-prev"
                aria-label="Previous photo"
              >
                <ChevronLeft className="w-8 h-8" strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  nextPhoto();
                }}
                className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 p-3 text-[#FAF9F6] hover:text-[#D4AF37] transition-colors"
                data-testid="lightbox-next"
                aria-label="Next photo"
              >
                <ChevronRight className="w-8 h-8" strokeWidth={1.5} />
              </button>
            </>
          )}
          <figure
            className="max-w-5xl w-full max-h-[88vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={active.image_url}
              alt={active.alt_text || active.caption || album.title}
              className="max-w-full max-h-[80vh] object-contain rounded-sm shadow-xl"
            />
            {(active.caption || album.title) && (
              <figcaption className="mt-4 text-center max-w-2xl">
                {active.caption && (
                  <p className="font-serif italic text-[#FAF9F6] text-lg leading-relaxed">
                    {active.caption}
                  </p>
                )}
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mt-2">
                  {lightboxIdx + 1} / {photos.length} · {album.title}
                </p>
              </figcaption>
            )}
          </figure>
        </div>
      )}
    </div>
  );
}
